<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerText"
    button-location="top"
    @submit="loadServiceSurveys()"
  >
    <template #title>
      Service Surveys
    </template>

    <template #top-form>
      <div class="row align-items-center">
        <label class="col-sm-4 control-label"> Service </label>

        <div class="col-sm-3">
          <dropdown-list
            v-model="serviceType"
            :options="serviceTypeList"
          />
        </div>

        <div class="col-sm-5 pl-0">
          <p-button
            type="purple"
            round
            native-type="submit"
            :disabled="status.isLoading"
          >
            Search
          </p-button>

          <p-button
            type="yellow"
            round
            :disabled="status.isLoading"
            @click="resetForm"
          >
            Reset
          </p-button>
        </div>
      </div>

      <div class="row align-items-center">
        <pagination
          :is-loading="status.isLoading"
          :current-page="status.currentPage"
          :total-pages="status.totalPages"
          @load-page-data="loadServiceSurveys"
        />
      </div>
    </template>

    <template #content>
      <div class="survey-card custom-collapse">
        <el-collapse>
          <el-collapse-item
            v-for="(item, index) in serviceSurveyList"
            :key="index"
            :name="index + 1"
          >
            <template #title>
              <div class="collapse-title">
                <div class="left-text">
                  <span v-if="item.caller"> {{ item.caller.firstName }} {{ item.caller.lastName }} </span>
                  <span v-else> Unknown User </span>
                </div>

                <div class="right-text text-capitalize">
                  {{ util.getLabelByField(serviceTypeList, item.service) || item.service }}
                </div>
              </div>
            </template>

            <div class="survey-detail">
              <div class="row">
                <div class="col-4 detail-item">
                  <label> Service Created At </label>
                  <div>
                    {{ util.formatDateTime(item.dateOfService, 'MMM DD, YYYY', item.caller?.timeZone || defaultTimeZone) }}
                  </div>
                </div>

                <div class="col-4 detail-item">
                  <label> Phone Number </label>
                  <div>
                    {{ item.caller?.phoneNumber || item.phoneNumber }}
                  </div>
                </div>

                <div class="col-4 detail-item">
                  <label> Email </label>
                  <div>
                    {{ item.caller?.email || item.email }}
                  </div>
                </div>

                <div
                  v-for="(questionItem, questionIndex) in item.data"
                  :key="questionIndex"
                  class="col-12 detail-item"
                >
                  <label>
                    {{ questionItem.question }}
                  </label>
                  <div
                    v-if="questionItem.value"
                    v-dompurify-html="util.convertTextToHtml(questionItem.value)"
                  />
                  <template v-if="questionItem.answers">
                    <div
                      v-for="(answer, answerIndex) in questionItem.answers"
                      :key="answerIndex"
                    >
                      {{ answer }}
                    </div>
                  </template>

                  <template v-if="questionItem.details">
                    <label>
                      {{ questionItem.details.statement }}
                    </label>

                    <div v-dompurify-html="util.convertTextToHtml(questionItem.details.value!.join('\n'))" />
                  </template>
                </div>

                <div class="col-12 detail-item">
                  <label> Operator Notes </label>
                  <div v-dompurify-html="util.convertTextToHtml(item.notes ?? '')" />
                </div>
              </div>
            </div>

            <div class="form-submit-button-group pt-10 pb-10">
              <p-button
                type="purple"
                round
                @click="onAddOperatorNote(item)"
              >
                Add Operator Note
              </p-button>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive } from 'vue';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { ServiceSurveyType } from '@/models/survey';
import { ListType, ServiceTypeEnum } from '@/models/app';
import { defaultTimeZone } from '@/constants/app';
import { serviceSurveyService } from '@/services/serviceSurvey';

const pageId = 'service-surveys';
const paginationLimit = 10;

const serviceTypeList: ListType[] = [
  { id: 1, key: '', label: 'All' },
  { id: 2, key: ServiceTypeEnum.Ride, label: 'Ride' },
  { id: 3, key: ServiceTypeEnum.Grocery, label: 'Grocery' },
  { id: 4, key: ServiceTypeEnum.Meal, label: 'Meal Delivery' },
  { id: 5, key: ServiceTypeEnum.HomeService, label: 'Home Service' },
  { id: 6, key: ServiceTypeEnum.Pharmacy, label: 'Medication Delivery' },
  { id: 7, key: ServiceTypeEnum.VirtualEvent, label: 'Virtual Event' },
  { id: 8, key: ServiceTypeEnum.Reimburse, label: 'Reimburse' },
  { id: 9, key: ServiceTypeEnum.Other, label: 'Other' },
];

const { showConfirmPopupDialogWithValidation, showErrorPopupDialog } = useSwalDialog();

const status = reactive({
  isLoading: false,
  spinnerText: 'Loading Service Surveys...',
  currentPage: 1,
  totalPages: 1,
});

const serviceSurveyList: Ref<ServiceSurveyType[]> = ref([]);
const serviceType = ref('');

const loadServiceSurveys = async (currentPage: number = 1) => {
  status.currentPage = currentPage;
  status.spinnerText = 'Loading Service Surveys...';
  status.isLoading = true;

  try {
    const response = await serviceSurveyService.fetchServiceSurveys({
      service: serviceType.value,
      pagination: {
        limit: paginationLimit,
        offset: (currentPage - 1) * paginationLimit,
      },
    });

    status.totalPages = Math.ceil(response.count / paginationLimit) || 1;
    serviceSurveyList.value = response.rows;
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

const onAddOperatorNote = async (item: ServiceSurveyType) => {
  const operatorNotes = await showConfirmPopupDialogWithValidation({
    title: 'Add Operator Note',
    input: 'textarea',
    inputPlaceholder: 'Please input notes',
    validationText: 'Please input notes',
    confirmButtonText: 'Add Note',
  });

  if (!operatorNotes) {
    return;
  }

  status.spinnerText = 'Adding Operator Note...';
  status.isLoading = true;

  try {
    await serviceSurveyService.addSurveyOperatorNote({
      id: item.id,
      notes: operatorNotes,
    });

    await loadServiceSurveys(status.currentPage);
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

const resetForm = () => {
  serviceType.value = '';
  serviceSurveyList.value = [];
};

loadServiceSurveys();
</script>

<style lang="scss">
.service-surveys {
  .survey-card {
    .survey-detail {
      padding: 20px 20px 0;
      font-size: 16px;
      color: #151515;
    }

    .detail-item {
      label {
        font-weight: bold;
        color: #151515;
        margin-top: 10px;
        margin-bottom: 0;
      }

      div {
        font-style: italic;
      }
    }
  }
}
</style>
